/**
 * Enhanced Filter Row Component
 *
 * Renders type-aware filter inputs as a table row (first row after headers)
 * Similar to the existing DataTable filter row but with proper data type controls
 */

import React, { useCallback, useMemo, useRef, useState } from "react";
import { TableCell, Popover, Checkbox } from "@mui/material";
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { getFilterConfig, validateFilterValue, convertFilterValue, FILTER_TYPES } from "../../utils/filterConfig";
import "./EnhancedFilterRow.scss";

function MultiSelectDropdown({ columnKey, config, selectedValues, label, isDark, commonProps, onApply }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = useRef(null);
  const open = Boolean(anchorEl);

  const toggleOption = useCallback(
    optionValue => {
      const strVal = String(optionValue);
      const next = selectedValues.includes(strVal)
        ? selectedValues.filter(v => v !== strVal)
        : [...selectedValues, strVal];
      onApply(columnKey, next);
    },
    [columnKey, selectedValues, onApply]
  );

  const clearAll = useCallback(() => {
    onApply(columnKey, []);
    setAnchorEl(null);
  }, [columnKey, onApply]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        {...commonProps}
        className={`${commonProps.className} enhanced-filter-row__multiselect-btn`}
        onClick={e => setAnchorEl(e.currentTarget)}
        title={config.description}
      >
        {label}
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            className: `enhanced-filter-row__multiselect-popover ${isDark ? "dark" : "light"}`,
          },
        }}
      >
        <div className="enhanced-filter-row__multiselect-list">
          {config.options?.map(option => {
            const strVal = String(option.value);
            const checked = selectedValues.includes(strVal);
            return (
              <label key={strVal} className="enhanced-filter-row__multiselect-item">
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={() => toggleOption(option.value)}
                  sx={{
                    padding: "2px",
                    color: isDark ? "#4b5563" : "#d1d5db",
                    "&.Mui-checked": { color: "var(--color-accent-active, #f47c20)" },
                  }}
                />
                <span className="enhanced-filter-row__multiselect-label">{option.label}</span>
              </label>
            );
          })}
        </div>
        {selectedValues.length > 0 && (
          <button type="button" className="enhanced-filter-row__multiselect-clear" onClick={clearAll}>
            Clear all
          </button>
        )}
      </Popover>
    </>
  );
}

MultiSelectDropdown.propTypes = {
  columnKey: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  selectedValues: PropTypes.array.isRequired,
  label: PropTypes.string.isRequired,
  isDark: PropTypes.bool,
  commonProps: PropTypes.object,
  onApply: PropTypes.func.isRequired,
};

export default function EnhancedFilterRow({
  columns = [],
  filters = {},
  visibleColumns = null,
  onFiltersChange = () => {},
  onFilterKeyDown = () => {},
  isDark = false,
}) {
  // Local state for range inputs and text inputs to avoid triggering API calls on every keystroke
  const [rangeInputs, setRangeInputs] = useState({});
  const [textInputs, setTextInputs] = useState({});

  // Get filterable columns that are visible
  const filterableColumns = useMemo(() => {
    let filtered = columns.filter(col => col.filterable !== false);

    // If visibleColumns is provided, only show filters for visible columns
    if (visibleColumns && typeof visibleColumns === "object") {
      filtered = filtered.filter(col => visibleColumns[col.key] === true);
    }

    return filtered;
  }, [columns, visibleColumns]);

  // Handle text input change (updates local state only, no API call)
  const handleTextInputChange = useCallback((columnKey, value) => {
    setTextInputs(prev => ({ ...prev, [columnKey]: value }));
  }, []);

  // Apply filter to parent (triggers API call via debounce)
  const applyFilter = useCallback(
    (columnKey, value) => {
      if (!validateFilterValue(columnKey, value)) {
        return; // Reject invalid values
      }

      const converted = convertFilterValue(columnKey, value);
      const newFilters = { ...filters };

      if (converted === null || converted === "") {
        delete newFilters[columnKey];
      } else {
        newFilters[columnKey] = converted;
      }

      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  // Handle Enter key press for text filters
  const handleTextFilterKeyDown = useCallback(
    (e, columnKey) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const value = e.target.value;
        // Apply the filter
        applyFilter(columnKey, value);
        // Call the parent's onFilterKeyDown handler to flush the sync
        onFilterKeyDown(e);
      }
    },
    [applyFilter, onFilterKeyDown]
  );

  // Render filter input based on type
  const renderFilterInput = column => {
    const config = getFilterConfig(column.key);

    const commonProps = {
      className: "enhanced-filter-row__input",
      "data-filter-type": config.type,
    };

    switch (config.type) {
      case FILTER_TYPES.TEXT: {
        // Use local text input state for real-time feedback, but don't trigger API calls
        const localValue = textInputs[column.key] ?? filters[column.key] ?? "";
        return (
          <input
            {...commonProps}
            type="text"
            placeholder={config.placeholder}
            value={localValue}
            onChange={e => handleTextInputChange(column.key, e.target.value)}
            onKeyDown={e => handleTextFilterKeyDown(e, column.key)}
            title={`${config.description} (Press Enter to apply)`}
          />
        );
      }

      case FILTER_TYPES.NUMBER: {
        const localValue = textInputs[column.key] ?? filters[column.key] ?? "";
        return (
          <input
            {...commonProps}
            type="number"
            placeholder={config.placeholder}
            value={localValue}
            onChange={e => handleTextInputChange(column.key, e.target.value)}
            onKeyDown={e => handleTextFilterKeyDown(e, column.key)}
            min={config.min}
            max={config.max}
            title={`${config.description} (Press Enter to apply)`}
          />
        );
      }

      case FILTER_TYPES.SELECT: {
        const localValue = filters[column.key] ?? "";
        return (
          <select
            {...commonProps}
            value={String(localValue)}
            onChange={e => applyFilter(column.key, e.target.value)}
            title={config.description}
          >
            <option value="">All</option>
            {config.options?.map(option => (
              <option key={option.value} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      case FILTER_TYPES.MULTISELECT: {
        const selectedValues = Array.isArray(filters[column.key]) ? filters[column.key] : [];
        const count = selectedValues.length;
        const label =
          count === 0
            ? "All"
            : count === 1
              ? (config.options?.find(o => String(o.value) === String(selectedValues[0]))?.label ?? selectedValues[0])
              : `${count} selected`;

        return (
          <MultiSelectDropdown
            columnKey={column.key}
            config={config}
            selectedValues={selectedValues}
            label={label}
            isDark={isDark}
            commonProps={commonProps}
            onApply={(key, values) => applyFilter(key, values.length > 0 ? values : "")}
          />
        );
      }

      case FILTER_TYPES.BOOLEAN: {
        const localValue = filters[column.key] ?? "";
        return (
          <select
            {...commonProps}
            value={localValue}
            onChange={e => applyFilter(column.key, e.target.value)}
            title={config.description}
          >
            <option value="">All</option>
            {config.options?.map(option => (
              <option key={String(option.value)} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      case FILTER_TYPES.DATE: {
        const localValue = filters[column.key] ?? "";
        return (
          <input
            {...commonProps}
            type="date"
            value={localValue}
            onChange={e => applyFilter(column.key, e.target.value)}
            title={config.description}
          />
        );
      }

      case FILTER_TYPES.RANGE: {
        // Use local state for range inputs to avoid triggering API on every keystroke
        const rangeValue = filters[column.key];

        // Initialize local state: use local state if available, otherwise use filter values
        const localRange = rangeInputs[column.key] || {
          min: rangeValue?.min ?? "",
          max: rangeValue?.max ?? "",
        };

        // Get current values from local state
        const minVal = localRange.min;
        const maxVal = localRange.max;

        const handleRangeChange = (field, value) => {
          // Preserve both min and max values when updating either one
          const newLocal = {
            min: field === "min" ? value : localRange.min,
            max: field === "max" ? value : localRange.max,
          };
          setRangeInputs(prev => ({ ...prev, [column.key]: newLocal }));
        };

        const handleRangeSubmit = () => {
          const newRange = {
            min: localRange.min !== "" ? Number(localRange.min) : "",
            max: localRange.max !== "" ? Number(localRange.max) : "",
          };
          applyFilter(column.key, newRange);
          // Clear local state after submitting
          setRangeInputs(prev => {
            const updated = { ...prev };
            delete updated[column.key];
            return updated;
          });
        };

        const handleKeyPress = e => {
          if (e.key === "Enter") {
            handleRangeSubmit();
          }
        };

        return (
          <div
            className="enhanced-filter-row__range-inputs"
            style={{ display: "flex", gap: "8px", alignItems: "center" }}
          >
            <input
              type="number"
              className="enhanced-filter-row__input"
              placeholder="Min"
              value={minVal}
              min={config.min}
              max={config.max}
              step={config.step || 1}
              onChange={e => handleRangeChange("min", e.target.value)}
              onKeyDown={handleKeyPress}
              title={`${config.description} - Min (press Enter to apply)`}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: "12px" }}>to</span>
            <input
              type="number"
              className="enhanced-filter-row__input"
              placeholder="Max"
              value={maxVal}
              min={config.min}
              max={config.max}
              step={config.step || 1}
              onChange={e => handleRangeChange("max", e.target.value)}
              onKeyDown={handleKeyPress}
              title={`${config.description} - Max (press Enter to apply)`}
              style={{ flex: 1 }}
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <motion.tr
      className={`enhanced-filter-row ${isDark ? "dark" : "light"}`}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
    >
      {/* Empty cell for checkbox column alignment — only if parent has a checkbox column */}
      {columns.some(col => col.key === "__select") && (
        <TableCell className="enhanced-filter-row__cell" component="td" style={{ width: "48px", padding: "8px" }} />
      )}
      {filterableColumns.map(col => (
        <TableCell key={col.key} className="enhanced-filter-row__cell" component="td">
          {renderFilterInput(col)}
        </TableCell>
      ))}
    </motion.tr>
  );
}

EnhancedFilterRow.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      filterable: PropTypes.bool,
    })
  ),
  filters: PropTypes.object,
  visibleColumns: PropTypes.object,
  onFiltersChange: PropTypes.func,
  onFilterKeyDown: PropTypes.func,
  isDark: PropTypes.bool,
};
